import { isEcommerceAvailable, isLikeForLikeAvailable } from '#shared/filter.utils.js';
export class PermissionService {
    userScope;
    selectedFilters;
    storePerimeter;
    taxonomy;
    constructor({ userScope, selectedFilters, taxonomy, storePerimeter }) {
        if (!userScope) {
            throw new Error('PermissionService requires a userScope');
        }
        this.userScope = userScope;
        this.selectedFilters = selectedFilters;
        this.storePerimeter = storePerimeter;
        this.taxonomy = taxonomy;
    }
    hasNielsen() {
        return this.userScope.organization.country && ['FRA'].includes(this.userScope.organization.country);
    }
    hasAccessToSalesAmount() {
        return this.userScope.permissions.kpis.includes('sales');
    }
    hasAccessToSalesQuantity() {
        return this.userScope.permissions.kpis.includes('quantity');
    }
    hasAccessToSalesPossessionRate() {
        return this.userScope.permissions.kpis.includes('salesPossessionRate') && this.taxonomy === 'carrefour';
    }
    hasAccessToGrossServiceRate() {
        return this.userScope.permissions.kpis.includes('grossServiceRate') && this.taxonomy === 'carrefour';
    }
    hasAccessToNetServiceRate() {
        return this.userScope.permissions.kpis.includes('netServiceRate') && this.taxonomy === 'carrefour';
    }
    hasAccessToSupplierMarketShare() {
        return this.userScope.permissions.kpis.includes('carrefourMarketShare') && this.taxonomy === 'carrefour';
    }
    hasAccessToNielsenReferential() {
        return this.userScope.permissions.kpis.includes('nielsenMarketShare');
    }
    hasAccessToNielsenMarketShare() {
        return (this.hasAccessToNielsenReferential() &&
            this.taxonomy === 'nielsen' &&
            this.userScope.organization.country &&
            ['FRA'].includes(this.userScope.organization.country));
    }
    hasAccessToStorePerimeter() {
        return this.userScope.permissions.kpis.includes('likeForLike');
    }
    hasAccessToStoresRegion() {
        return this.userScope.permissions.kpis.includes('region');
    }
    hasAccessToWarehouseStock() {
        return this.userScope.permissions.kpis.includes('warehouseStock') && this.taxonomy === 'carrefour';
    }
    hasAccessToStoreStock() {
        return this.userScope.permissions.kpis.includes('storeStock') && this.taxonomy === 'carrefour';
    }
    hasAccessToTotalStock() {
        return this.hasAccessToStoreStock() && this.hasAccessToWarehouseStock();
    }
    hasAccessToBusinessEquationAllCustomers() {
        return this.userScope.permissions.kpis.includes('businessEquationAllCustomers') && this.taxonomy === 'carrefour';
    }
    hasAccessToBusinessEquationCardholdersNonCardholders() {
        return (this.userScope.permissions.kpis.includes('businessEquationCardholdersNonCardholders') &&
            this.taxonomy === 'carrefour');
    }
    hasAccessToPossessionZoomPage() {
        return (this.userScope.permissions.kpis.includes('possessionZoomPage') &&
            this.taxonomy === 'carrefour' &&
            this.storePerimeter !== 'likeForLike');
    }
    hasAccessToPossessionZoomPageWithTheoretical() {
        return (this.userScope.permissions.kpis.includes('possessionZoomPageWithTheoretical') &&
            this.taxonomy === 'carrefour' &&
            this.storePerimeter !== 'likeForLike');
    }
    hasAccessToAssortmentTypology() {
        return this.userScope.permissions.kpis.includes('assortmentTypology') && this.taxonomy === 'carrefour';
    }
    hasAccessToProductTypology() {
        return this.userScope.permissions.kpis.includes('productTypology') && this.taxonomy === 'carrefour';
    }
    /**
     * Tells whether the user can select like for like given the selected purchase channels.
     *
     * @deprecated Driven by the database in the new filters (FilterDefinitionValueRestriction).
     * TODO: [FILTER] Remove this once the new filters are done.
     */
    hasAccessToLikeForLike() {
        if (!this.hasAccessToStorePerimeter()) {
            return false;
        }
        return isLikeForLikeAvailable(this.selectedFilters?.activityTypesFilters ?? [], this.userScope.organization.country);
    }
    /**
     * Tells whether the user can select the e-commerce purchase channel given the store perimeter.
     *
     * @deprecated Driven by the database in the new filters (FilterDefinitionValueRestriction).
     * TODO: [FILTER] Remove this once the new filters are done.
     */
    hasAccessToEcommerce() {
        return isEcommerceAvailable(this.storePerimeter, this.userScope.organization.country);
    }
    hasAccessToDnDvZoomPage() {
        return ((this.userScope.permissions.kpis.includes('numericalDistribution') ||
            this.userScope.permissions.kpis.includes('weightedDistribution')) &&
            this.taxonomy === 'carrefour');
    }
    hasNonPossessionRate() {
        return this.userScope.permissions.kpis.includes('nonPossessed');
    }
    hasAccessToCompetitorVision() {
        return this.userScope.permissions.kpis.includes('competitorVision') && this.taxonomy === 'carrefour';
    }
    hasAccessToDynamicClient() {
        return this.userScope.permissions.kpis.includes('dynamicClientZoomPage') && this.taxonomy === 'carrefour';
    }
    hasAccessToCustomerSegmentation() {
        return this.userScope.permissions.kpis.includes('customerSegmentation') && this.taxonomy === 'carrefour';
    }
    hasAccessToCompetitorVisionPromo() {
        return this.userScope.permissions.kpis.includes('competitorVisionPromo') && this.taxonomy === 'carrefour';
    }
    hasAccessToDateGranularity() {
        return this.userScope.permissions.kpis.includes('dateGranularity');
    }
    isAdmin() {
        return Boolean(this.userScope.flags.isAdmin);
    }
    isAccountManager() {
        return Boolean(this.userScope.flags.isAccountManager);
    }
    isOrganizationsCreator() {
        return Boolean(this.userScope.flags.isOrganizationsCreator);
    }
    isProjectsAuthorized() {
        return this.userScope.permissions.features.includes('projects');
    }
}
//# sourceMappingURL=permissionService.class.js.map